import { apiClient } from './client';
import type { TipTapDoc } from '@/components/richtext/TipTapRenderer';
import type { SopFamily } from '@/api/sops.api';

/**
 * Client for the trainee-facing training surfaces (ADR-025, Stream C):
 *
 *   - `/api/training/me/*`          — dashboard, course player, lesson
 *                                     completes, quiz attempts, certificates
 *                                     (server/src/routes/training/me.routes.ts)
 *   - `/api/training/me/sops/*` and `/api/training/me/acks`
 *                                   — SOP acknowledgments
 *                                     (server/src/routes/training/acks.routes.ts)
 *
 * Shapes are mirrored 1:1 from the route payloads — keep them in
 * lockstep when the server changes. Authoring/reporting clients live in
 * `trainingAdmin.api.ts`.
 */

// ── Shared shapes ───────────────────────────────────────────────────────

export type LessonContentKind = 'rich_text' | 'sop';
export type AckSource = 'sop_page' | 'lesson' | 'checklist';

export interface QuizOption {
  id: string;
  text: string;
}

/** `[{kind, url, title?}]` — the lesson mediaLinks/attachmentLinks blobs. */
export interface LessonLink {
  kind: 'video' | 'link';
  url: string;
  title?: string;
}

/** Shared by lesson-complete and quiz-attempt responses. */
export interface FinalizeOutcome {
  courseCompleted: boolean;
  certificateCode: string | null;
  completionId: string | null;
}

// ── GET /api/training/me — my training dashboard ────────────────────────

export interface MyCourseSummary {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  category: string | null;
  version: number;
}

export interface MyEnrollment {
  id: string;
  courseId: string;
  course: MyCourseSummary;
  cycleNumber: number;
  dueAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  isOverdue: boolean;
  progress: { completedLessons: number; totalLessons: number };
}

export interface PendingSopAck {
  sopId: string;
  slug: string;
  family: SopFamily;
  number: number;
  title: string;
  sopVersionId: string;
  revision: number;
}

export interface MyCertificate {
  id: string;
  courseId: string;
  courseTitleSnapshot: string;
  courseVersion: number;
  quizScorePercent: number | null;
  completedAt: string;
  expiresAt: string | null;
  certificateCode: string;
}

export interface MyTrainingPayload {
  enrollments: MyEnrollment[];
  pendingSopAcks: PendingSopAck[];
  certificates: MyCertificate[];
}

export async function getMyTraining(): Promise<MyTrainingPayload> {
  const res = await apiClient.get<{ data: MyTrainingPayload }>('/api/training/me');
  return res.data.data;
}

// ── GET /api/training/me/courses/:courseId — player payload ─────────────

export interface PlayerLesson {
  id: string;
  title: string;
  order: number;
  contentKind: LessonContentKind;
  /** For sop-lessons this is the latest APPROVED revision's content. */
  contentJson: TipTapDoc | null;
  sop: { id: string; slug: string; title: string; approvedRevision: number | null } | null;
  mediaLinks: LessonLink[];
  attachmentLinks: LessonLink[];
  estimatedMinutes: number | null;
}

/** Taker-sanitized question — the server never sends `correctOptionId` here. */
export interface TakerQuizQuestion {
  id: string;
  quizId: string;
  order: number;
  prompt: string;
  optionsJson: QuizOption[];
  explanation: string | null;
}

export interface TakerQuiz {
  id: string;
  courseId: string;
  /** Null = the course's final quiz; set = a per-lesson quiz. */
  lessonId: string | null;
  title: string;
  passThresholdPercent: number;
  /** Null = unlimited attempts. */
  maxAttempts: number | null;
  questions: TakerQuizQuestion[];
}

export interface CoursePlayerPayload {
  course: MyCourseSummary & { passThresholdPercentDefault: number };
  enrollment: {
    id: string;
    cycleNumber: number;
    dueAt: string | null;
    startedAt: string | null;
    completedAt: string | null;
    expiresAt: string | null;
  };
  lessons: PlayerLesson[];
  quizzes: TakerQuiz[];
  lessonCompletions: { lessonId: string; completedAt: string }[];
}

export async function getCoursePlayer(courseId: string): Promise<CoursePlayerPayload> {
  const res = await apiClient.get<{ data: CoursePlayerPayload }>(
    `/api/training/me/courses/${encodeURIComponent(courseId)}`,
  );
  return res.data.data;
}

// ── POST /api/training/me/lessons/:lessonId/complete ────────────────────

export interface CompleteLessonResult extends FinalizeOutcome {
  lessonId: string;
  enrollmentId: string;
  completedAt: string;
}

export async function completeLesson(lessonId: string): Promise<CompleteLessonResult> {
  const res = await apiClient.post<{ data: CompleteLessonResult }>(
    `/api/training/me/lessons/${encodeURIComponent(lessonId)}/complete`,
  );
  return res.data.data;
}

// ── POST /api/training/me/quizzes/:quizId/attempts ──────────────────────

export interface QuizAnswerInput {
  questionId: string;
  selectedOptionId: string;
}

/** Graded per-question snapshot — mirrors the server's grade.ts contract. */
export interface AnswerSnapshotEntry {
  questionId: string;
  prompt: string;
  chosenOptionId: string | null;
  chosenOptionText: string | null;
  correctOptionId: string;
  correctOptionText: string | null;
  correct: boolean;
}

export interface QuizAttemptResult extends FinalizeOutcome {
  attemptId: string;
  attemptNumber: number;
  scorePercent: number;
  passed: boolean;
  answersSnapshot: AnswerSnapshotEntry[];
}

export async function submitQuizAttempt(
  quizId: string,
  body: { answers: QuizAnswerInput[]; enrollmentId?: string },
): Promise<QuizAttemptResult> {
  const res = await apiClient.post<{ data: QuizAttemptResult }>(
    `/api/training/me/quizzes/${encodeURIComponent(quizId)}/attempts`,
    body,
  );
  return res.data.data;
}

// ── SOP acknowledgments ─────────────────────────────────────────────────

export interface SopAckRecord {
  id: string;
  sopId: string;
  sopVersionId: string;
  membershipId: string | null;
  ackNameSnapshot: string;
  ackEmailSnapshot: string;
  ackRoleSnapshot: string;
  statementText: string;
  source: AckSource;
  acknowledgedAt: string;
}

export interface MyAck extends SopAckRecord {
  sop: { id: string; slug: string; family: SopFamily; number: number; title: string };
  sopVersion: { revision: number };
}

/**
 * Acknowledge the latest APPROVED revision of a SOP. Idempotent —
 * `created: false` means this revision was already signed.
 */
export async function acknowledgeSop(
  sopId: string,
  body: { source?: AckSource; lessonId?: string | null; checklistItemId?: string | null } = {},
): Promise<{ ack: SopAckRecord; created: boolean }> {
  const res = await apiClient.post<{ data: SopAckRecord; created: boolean }>(
    `/api/training/me/sops/${encodeURIComponent(sopId)}/acknowledge`,
    body,
  );
  return { ack: res.data.data, created: res.data.created };
}

export async function getMyAcks(): Promise<MyAck[]> {
  const res = await apiClient.get<{ data: MyAck[] }>('/api/training/me/acks');
  return res.data.data;
}

/** Reporting rollup for one SOP — requires `view_training_reports`. */
export interface SopAcksSummary {
  sop: { id: string; slug: string; family: SopFamily; number: number; title: string };
  latestApprovedRevision: number | null;
  total: number;
  latestRevisionAcks: number;
  acks: {
    id: string;
    membershipId: string | null;
    ackNameSnapshot: string;
    ackEmailSnapshot: string;
    ackRoleSnapshot: string;
    statementText: string;
    source: AckSource;
    revision: number;
    acknowledgedAt: string;
  }[];
}

export async function getSopAcks(sopId: string): Promise<SopAcksSummary> {
  const res = await apiClient.get<{ data: SopAcksSummary }>(
    `/api/training/sops/${encodeURIComponent(sopId)}/acks`,
  );
  return res.data.data;
}

// ── Certificates ────────────────────────────────────────────────────────

export interface CertificateRecord {
  id: string;
  organizationId: string;
  courseId: string;
  enrollmentId: string | null;
  membershipId: string | null;
  traineeNameSnapshot: string;
  traineeEmailSnapshot: string;
  courseTitleSnapshot: string;
  courseVersion: number;
  quizScorePercent: number | null;
  completedAt: string;
  expiresAt: string | null;
  certificateCode: string;
}

export async function listMyCertificates(): Promise<(MyCertificate & { traineeNameSnapshot: string })[]> {
  const res = await apiClient.get<{ data: (MyCertificate & { traineeNameSnapshot: string })[] }>(
    '/api/training/me/certificates',
  );
  return res.data.data;
}

/** Verification lookup — self always; others need `view_training_reports`. */
export async function getCertificate(code: string): Promise<CertificateRecord> {
  const res = await apiClient.get<{ data: CertificateRecord }>(
    `/api/training/me/certificates/${encodeURIComponent(code)}`,
  );
  return res.data.data;
}
